import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  BOARD_SPOT,
  GROUND,
  HEIGHT,
  WORK_CANVAS_INSET,
  buildScrollLine,
  endingPieces,
} from '../components/MakerLine';
import { CANVAS_SIZE, CanvasWork, MAKE_WAYS, Tool, WAYS, toolAt } from '../components/MakerTools';
import './MyMaker.css';

// /mymaker: for makers, reached by the QR code with their canvas. A story,
// after the way gianlucaboccia.me tells his: the screen stands still and a pencil draws.
// One cobalt line runs along the bottom; the "camera" follows the pencil and
// every chapter has its own colour.
//
//   00 Hey maker   01 Your canvas   02 A memory   - scrolled through
//   03 Making it                                  - a menu
//   04 On the day                                 - the ending
//
// The first three chapters are scrolled: the line turns into a signature,
// your canvas and three memories. Let go anywhere and the page glides on by
// itself to the end of the drawing being made, so a short swipe per drawing
// is enough.
//
// At the bottom of the scroll the pencil has drawn a big canvas, and the
// story stops at a menu of ways of making. Picking one turns the pencil into
// its tool - a brush, a camera, scissors - which goes to work on the canvas
// (components/MakerTools.js). Then the menu comes back, the ones tried
// ticked off, to try another - or "Continue" to the ending: the tool is a
// pencil again and draws the board, and the canvas just made appears in the
// middle spot.
//
// Scrolling back up rewinds the line; coming back down draws it again.

// Each chapter's paper and ink - within the brand's one blue, reversed now
// and then for drama.
const COLOURS = [
  ['#f4f3ec', '#2e5bdb'],
  ['#e9e7dc', '#2e5bdb'],
  ['#2e5bdb', '#f4f3ec'],
  ['#f4f3ec', '#2e5bdb'],
  ['#2449b8', '#f4f3ec'],
];

const PX_PER_UNIT = 0.8; // how much scrolling one unit of line (or pause) takes
const IDLE_MS = 160; // this long without scrolling counts as letting go
const GLIDE_MS_PER_PX = 5; // the glide to the next rest: slow enough to watch
const GLIDE_MIN_MS = 900;
const GLIDE_MAX_MS = 2600;
const PENCIL_AT = 0.6; // where the pencil sits across the screen
const EASE = 0.1; // how softly the drawing catches up with the scroll
const DRAW_SPEED = 320; // units a second, when the pencil draws by itself
const MAKE_MS = 3400; // how long a way of making takes on the canvas
const HAND_EASE = 0.2; // how the pencil/tool flies to where it's needed
const START_X = 40;

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const easeInOut = (x) => -(Math.cos(Math.PI * x) - 1) / 2;

// Measures runs of path commands, each starting and ending on the ground, as
// one stretch of line from `x`: { d, parts: [{ d, length, startX }], length,
// endX }.
function measure(runs, x) {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.style.cssText = 'position:absolute;width:0;height:0;visibility:hidden';
  const probe = document.createElementNS(ns, 'path');
  svg.appendChild(probe);
  document.body.appendChild(svg);

  let at = x;
  let length = 0;
  const parts = runs.map((run) => {
    const d = `${run} V ${GROUND}`;
    const startX = at;
    probe.setAttribute('d', `M ${at} ${GROUND} ${d}`);
    const l = probe.getTotalLength();
    length += l;
    at = probe.getPointAtLength(l).x;
    return { d, length: l, startX };
  });
  svg.remove();
  return { d: parts.map((p) => p.d).join(' '), parts, length, endX: at };
}

function MyMaker() {
  const { t } = useTranslation();
  const list = (key) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  // The words, chapter by chapter. Chapter 2 has one line per memory drawn
  // (maker.directions: the sound of it, something they always say,
  // an inside joke), chapter 3 one per way of making.
  const chapters = list('maker.chapters').map((chapter, i) => {
    if (i === 2) {
      return { ...chapter, steps: list('maker.directions').map((text) => ({ text })) };
    }
    if (i === 3) {
      return {
        ...chapter,
        steps: [
          { text: t('maker.pick') },
          ...MAKE_WAYS.map((way) => ({ label: t(`maker.ways.${way}.name`), text: t(`maker.ways.${way}.example`) })),
        ],
      };
    }
    return { ...chapter, steps: (chapter.steps || []).map((text) => ({ text })) };
  });

  const pieces = useMemo(buildScrollLine, []);
  // The scrolled part of the line: { d, total, scroll, endX, canvasX, marks,
  // steps, rests } - its path, its length, how much scrolling it takes,
  // where it ends, where the inside of the work canvas starts, at each
  // piece's start how far along the scroll (s) and the line (l) are, where
  // each line of text comes in, and where the pencil rests.
  const [line, setLine] = useState(null);
  // The ending, once "Continue" is pressed: { d, length, spot: [x, y] }.
  const [ending, setEnding] = useState(null);
  // The ways of making tried, and the one on the canvas now.
  const [tried, setTried] = useState([]);
  const [making, setMaking] = useState(null); // way
  const [makeT, setMakeT] = useState(1);
  const [view, setView] = useState({ w: 600 });
  const [scrollStep, setScrollStep] = useState([0, 0]);
  const [progress, setProgress] = useState(0);
  const [drawing, setDrawing] = useState(false); // the pencil drawing by itself

  const pathRef = useRef(null);
  const worldRef = useRef(null);
  const pencilRef = useRef(null);
  const svgRef = useRef(null);

  // Measure the scrolled part once, in the browser.
  useLayoutEffect(() => {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.style.cssText = 'position:absolute;width:0;height:0;visibility:hidden';
    const probe = document.createElementNS(ns, 'path');
    svg.appendChild(probe);
    document.body.appendChild(svg);

    let x = START_X;
    let l = 0;
    let s = 0;
    const parts = [];
    const steps = [];
    const marks = [{ s: 0, l: 0 }];
    const rests = [0];
    let canvasX = 0;
    pieces.forEach((piece) => {
      if (piece.canvas) canvasX = x + WORK_CANVAS_INSET;
      if (piece.step) steps.push({ s, step: piece.step });
      if (piece.pause) {
        // Rest a little way into the pause: the drawing done, its words up.
        rests.push(s + Math.min(24, piece.pause / 4));
        s += piece.pause;
      } else {
        const d = `${piece.d} V ${GROUND}`;
        probe.setAttribute('d', `M ${x} ${GROUND} ${d}`);
        const length = probe.getTotalLength();
        parts.push(d);
        l += length;
        s += length;
        x = probe.getPointAtLength(length).x;
      }
      marks.push({ s, l });
    });
    svg.remove();
    rests.push(s);
    setLine({ d: `M ${START_X} ${GROUND} ${parts.join(' ')}`, total: l, scroll: s, endX: x, canvasX, marks, steps, rests });
  }, [pieces]);

  // The whole line as it stands: the scrolled part, and the ending.
  const full = useMemo(() => {
    if (!line) return null;
    return {
      d: ending ? `${line.d} ${ending.d}` : line.d,
      total: line.total + (ending?.length ?? 0),
    };
  }, [line, ending]);
  const totalRef = useRef(0);
  totalRef.current = full?.total ?? 0;

  // What the tick needs to know about the menu, without restarting it.
  const makingRef = useRef(null); // { way, start }
  const modeRef = useRef('line'); // 'line' | 'canvas' - where the hand works
  const endingRef = useRef(false);
  const workRefs = useRef([]);
  modeRef.current = making && !ending ? 'canvas' : 'line';
  endingRef.current = Boolean(ending);

  // How wide the drawing's window is, in its own units.
  useLayoutEffect(() => {
    const onResize = () => {
      const box = svgRef.current?.getBoundingClientRect();
      if (box && box.height) setView({ w: (HEIGHT * box.width) / box.height });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [line]);

  // Every frame: how much of the line is drawn, what the tool has done to the
  // canvas, where the pencil (or tool) and the camera are, and which words
  // show.
  const drawnRef = useRef(0);
  useEffect(() => {
    const path = pathRef.current;
    if (!line || !path) return undefined;
    const still = reduceMotion();

    let shown = 0;
    let camera = null;
    let frame = null;
    let last = performance.now();
    let lastStep = '';
    let lastDash = 0;
    let wasDrawing = false;
    let hand = null;
    let handTarget = null;
    let lastT = -1;

    const tick = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const total = totalRef.current;
      if (total !== lastDash) {
        path.style.strokeDasharray = `${total} ${total}`;
        lastDash = total;
      }

      const max = document.documentElement.scrollHeight - window.innerHeight;
      const target = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      shown = still ? target : shown + (target - shown) * EASE;
      if (Math.abs(target - shown) < 0.00005) shown = target;

      // The scrolled part: how much is drawn at this point of the scroll -
      // standing still during a pause.
      const at = shown * line.scroll;
      let base = line.total;
      for (let i = 1; i < line.marks.length; i += 1) {
        const a = line.marks[i - 1];
        const b = line.marks[i];
        if (at <= b.s) {
          base = b.s === a.s ? a.l : a.l + ((b.l - a.l) * (at - a.s)) / (b.s - a.s);
          break;
        }
      }

      // At the bottom of the scroll, the pencil carries on by itself through
      // whatever the menu added - at a drawing pace, not all at once.
      let drawn = base;
      const atBottom = target > 0.999 && base >= line.total - 0.5;
      if (atBottom && total > line.total) {
        const from = Math.max(drawnRef.current, base);
        drawn = still ? total : Math.min(total, from + DRAW_SPEED * dt);
      }
      drawn = Math.min(drawn, total);
      drawnRef.current = drawn;

      // A way of making at work on the canvas.
      const job = makingRef.current;
      let t = 1;
      if (job) {
        t = still ? 1 : Math.min(1, (now - job.start) / MAKE_MS);
        if (t !== lastT) {
          lastT = t;
          setMakeT(t);
        }
      }

      const busy = atBottom && (drawn < total - 0.5 || (job && t < 1 && modeRef.current === 'canvas'));
      if (busy !== wasDrawing) {
        wasDrawing = busy;
        setDrawing(busy);
      }

      path.style.strokeDashoffset = `${total - drawn}`;
      const tip = path.getPointAtLength(drawn);
      const onCanvas = atBottom && modeRef.current === 'canvas';

      // Where the hand goes: the tip of the line, or wherever the tool is
      // working on the canvas (staying put between moves).
      if (onCanvas && job && t >= 1) {
        // Done: the tool steps aside, next to the canvas, so all of it shows.
        handTarget = [line.canvasX + CANVAS_SIZE + 26, GROUND - 4];
      } else if (onCanvas && job) {
        const p = toolAt(job.way, t, workRefs);
        if (p) handTarget = [line.canvasX + p[0], GROUND - CANVAS_SIZE + p[1]];
        else if (!handTarget) handTarget = [tip.x, tip.y];
      } else {
        handTarget = [tip.x, tip.y];
      }
      if (!hand || still) hand = [...handTarget];
      else hand = [hand[0] + (handTarget[0] - hand[0]) * HAND_EASE, hand[1] + (handTarget[1] - hand[1]) * HAND_EASE];
      // While the line is drawing, the pencil is on its tip, exactly - once
      // it has got back there from the canvas.
      if (!onCanvas && Math.hypot(hand[0] - tip.x, hand[1] - tip.y) < 30) hand = [tip.x, tip.y];

      // The camera follows the pencil - or, at the menu, holds the canvas in
      // the middle of the screen.
      const atMenu = atBottom && !endingRef.current;
      const want = atMenu ? line.canvasX + CANVAS_SIZE / 2 - view.w / 2 : tip.x - view.w * PENCIL_AT;
      camera = camera === null || still ? want : camera + (want - camera) * 0.12;
      worldRef.current?.setAttribute('transform', `translate(${-camera} 0)`);
      pencilRef.current?.setAttribute('transform', `translate(${hand[0]} ${hand[1]})`);

      let current = line.steps[0].step;
      for (const s of line.steps) if (s.s <= at + 1) current = s.step;
      const key = current.join(':');
      if (key !== lastStep) {
        lastStep = key;
        setScrollStep(current);
      }
      setProgress((p) => (Math.abs(p - shown) > 0.002 || shown === 1 || shown === 0 ? shown : p));

      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // Not restarted when the ending is added: that would reset the drawing
    // and the camera for a moment. The tick reads it from endingRef.
  }, [line, view.w]);

  // Letting go of the scroll: glide on to the next rest in the direction of
  // travel.
  const glideTo = useRef(null);
  useEffect(() => {
    if (!line || reduceMotion()) return undefined;

    let idle = null;
    let glide = null;
    let lastY = window.scrollY;
    let direction = 1;
    let touching = false;

    const stopGlide = () => {
      if (glide) cancelAnimationFrame(glide);
      glide = null;
    };

    const toPx = (rest) => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return (rest / line.scroll) * max;
    };

    const glideToY = (target) => {
      stopGlide();
      const from = window.scrollY;
      const distance = target - from;
      if (Math.abs(distance) < 2) return;
      const ms = Math.min(GLIDE_MAX_MS, Math.max(GLIDE_MIN_MS, Math.abs(distance) * GLIDE_MS_PER_PX));
      const start = performance.now();
      const step = (now) => {
        const x = Math.min(1, (now - start) / ms);
        window.scrollTo({ top: from + distance * easeInOut(x), behavior: 'instant' });
        lastY = window.scrollY;
        glide = x < 1 ? requestAnimationFrame(step) : null;
      };
      glide = requestAnimationFrame(step);
    };
    glideTo.current = glideToY;

    const settle = () => {
      if (touching || glide) return;
      const y = window.scrollY;
      const rests = line.rests.map(toPx);
      if (rests.some((r) => Math.abs(r - y) < 3)) return;
      const next = direction > 0 ? rests.find((r) => r > y) : [...rests].reverse().find((r) => r < y);
      if (next !== undefined) glideToY(next);
    };

    const onScroll = () => {
      if (glide) return; // our own glide moving the page
      const y = window.scrollY;
      if (y !== lastY) direction = y > lastY ? 1 : -1;
      lastY = y;
      clearTimeout(idle);
      idle = setTimeout(settle, IDLE_MS);
    };

    // The visitor taking over: stop gliding and leave the page to them.
    const takeOver = () => {
      stopGlide();
      clearTimeout(idle);
    };
    const onTouchStart = () => {
      touching = true;
      takeOver();
    };
    const onTouchEnd = () => {
      touching = false;
      clearTimeout(idle);
      idle = setTimeout(settle, IDLE_MS);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('wheel', takeOver, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', takeOver);
    return () => {
      stopGlide();
      clearTimeout(idle);
      glideTo.current = null;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', takeOver);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', takeOver);
    };
  }, [line]);

  // ---------------------------------------------------------------------------
  // Where the story is
  // ---------------------------------------------------------------------------

  const atBottom = progress > 0.995;
  const lastWay = making;
  const menuOpen = atBottom && !ending && !drawing;
  const done = atBottom && ending && !drawing;

  // At the bottom the menu decides the words; above it, the scroll does.
  let step = scrollStep;
  if (atBottom && ending) step = [4, drawing ? 0 : 1];
  else if (atBottom && lastWay) step = [3, 1 + MAKE_WAYS.indexOf(lastWay)];

  const pick = (way) => {
    if (drawing) return;
    makingRef.current = { way, start: performance.now() };
    workRefs.current = [];
    setMaking(way);
    setMakeT(0);
    setTried((list) => (list.includes(way) ? list : [...list, way]));
    setDrawing(true);
  };
  const finish = () => {
    const m = measure(endingPieces(), line.endX);
    const board = m.parts[1].startX;
    setEnding({ d: m.d, length: m.length, spot: [board + BOARD_SPOT.x, GROUND + BOARD_SPOT.y] });
    setDrawing(true);
  };
  const restart = () => {
    setEnding(null);
    setMaking(null);
    setTried([]);
    makingRef.current = null;
    drawnRef.current = 0;
    if (glideTo.current) glideTo.current(0);
    else window.scrollTo({ top: 0 });
  };
  // The hand: a pencil on the line, the way of making's tool on the canvas.
  const toolName = atBottom && making && !ending ? WAYS[making].tool : 'pencil';

  const [chapterIndex, lineIndex] = step;
  const chapter = chapters[chapterIndex] ?? { steps: [] };
  const words = chapter.steps[lineIndex] ?? chapter.steps[0] ?? { text: '' };
  const [paper, ink] = COLOURS[chapterIndex] ?? COLOURS[0];

  const height = line ? `calc(${Math.round(line.scroll * PX_PER_UNIT)}px + 100vh)` : '100vh';

  let cue;
  if (done) {
    cue = (
      <button type="button" className="m2-cue" onClick={restart}>
        {t('maker.back')} <span aria-hidden="true">↑</span>
      </button>
    );
  } else if (atBottom && !ending) {
    cue = (
      <button type="button" className="m2-cue m2-cue--next" onClick={finish} disabled={drawing}>
        {t('maker.next')} <span aria-hidden="true">→</span>
      </button>
    );
  } else if (!atBottom) {
    cue = (
      <p className="m2-cue">
        {t('maker.keep')} <span className="m2-arrow" aria-hidden="true">↓</span>
      </p>
    );
  } else {
    cue = <span />;
  }

  return (
    <div
      className={`atelier m2 ${chapterIndex === 2 || chapterIndex === 4 ? 'is-dark' : ''}`}
      style={{ '--paper': paper, '--fg': ink, height }}
    >
      <div className="m2-stage">
        <header className="m2-top">
          <Link to="/" className="m2-brand">
            <img className="brand-logo" src="/logo.svg" alt="myMosaik" />
          </Link>
          <LanguageSwitcher />
        </header>

        <section className="m2-copy" aria-live="polite">
          <div className="m2-head" key={`c${chapterIndex}`}>
            <p className="m2-kicker">{chapter.kicker}</p>
            <h1 className="h-display m2-title">{chapter.title}</h1>
          </div>
          <div className="m2-side">
            <div className="m2-words" key={`s${chapterIndex}:${lineIndex}`}>
              {words.label && <p className="m2-label">{words.label}</p>}
              <p className={chapterIndex === 2 ? 'm2-memory' : 'm2-text'}>{words.text}</p>
            </div>

            {/* The ways of making: pick one and the line carries on with it. */}
            {menuOpen && (
              <div className="m2-menu">
                {tried.length > 0 && <p className="m2-more">{t('maker.more')}</p>}
                <div className="m2-ways" role="group" aria-label={chapter.title}>
                  {MAKE_WAYS.map((way) => {
                    const has = tried.includes(way);
                    return (
                      <button
                        key={way}
                        type="button"
                        className={`m2-way ${has ? 'is-tried' : ''} ${way === lastWay ? 'is-last' : ''}`}
                        onClick={() => pick(way)}
                      >
                        {has && <span aria-hidden="true">✓ </span>}
                        {t(`maker.ways.${way}.name`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        <svg
          ref={svgRef}
          className="m2-drawing"
          viewBox={`0 0 ${view.w} ${HEIGHT}`}
          preserveAspectRatio="xMinYMid meet"
          aria-hidden="true"
          focusable="false"
        >
          <g ref={worldRef}>
            {full && <path ref={pathRef} className="m2-line" d={full.d} />}

            {/* What the tool has done to the work canvas. */}
            {line && making && atBottom && (
              <g transform={`translate(${line.canvasX} ${GROUND - CANVAS_SIZE})`}>
                <CanvasWork key={making} way={making} t={makeT} label={t('maker.ticket')} refs={workRefs} />
              </g>
            )}

            {/* On the board, the middle spot: the canvas just made. */}
            {ending && done && (
              <g
                className="m2-spot"
                transform={`translate(${ending.spot[0]} ${ending.spot[1]}) scale(${BOARD_SPOT.size / CANVAS_SIZE})`}
              >
                {making ? (
                  <CanvasWork way={making} t={1} label={t('maker.ticket')} />
                ) : (
                  <path className="m2c-ink" d="M75 112 C30 84 40 34 75 60 C110 34 120 84 75 112 Z" />
                )}
              </g>
            )}

            <g ref={pencilRef}>
              <Tool name={toolName} />
            </g>
          </g>
        </svg>

        {/* Before the first scroll: what to do here. */}
        <div className={`m2-start ${progress > 0.004 ? 'is-gone' : ''}`} aria-hidden={progress > 0.004}>
          <span className="m2-mouse" aria-hidden="true" />
          {t('maker.scroll')}
        </div>

        <footer className="m2-bar">
          <p className="m2-chapter">
            <span>0{chapterIndex}</span> {chapter.name}
          </p>
          {cue}
          <span className="m2-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />
        </footer>
      </div>
    </div>
  );
}

export default MyMaker;
